import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  TablePagination,
  Skeleton,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  Button,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Inbox,
  RefreshCw,
  Eye,
  ChevronDown,
  X,
  EyeIcon,
  RotateCwIcon,
} from "lucide-react";
import { useGetList } from "../../../../hooks/useGetList";
import { useDispatch, useSelector } from "react-redux";
import {
  setLimit,
  setPage,
} from "../../../../redux/purchaseRequest/purchaseRequestSlice";

import formatDate from "../../../../Utils/formatDate";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import buildParams from "../../../../Utils/buildParams";

// Skeleton Loader Component
const SkeletonLoader = () => (
  <>
    {[...Array(5)].map((_, index) => (
      <TableRow key={index}>
        <TableCell>
          <Skeleton variant="text" width={80} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={120} />
          <Skeleton variant="text" width={80} height={16} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={150} />
          <Skeleton variant="text" width={100} height={16} />
          <Skeleton variant="text" width={80} height={16} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={80} />
        </TableCell>
        <TableCell>
          <Skeleton variant="rectangular" width={80} height={24} />
        </TableCell>
      </TableRow>
    ))}
  </>
);

// Empty State Component
const EmptyState = ({ onRefresh }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    py={8}
    minHeight={400}
  >
    <Inbox
      size={64}
      style={{
        color: "text.secondary",
        marginBottom: 16,
      }}
    />
    <Typography variant="h6" color="text.secondary" gutterBottom>
      No Purchase Requests Found
    </Typography>
    <Typography variant="body2" color="text.secondary" align="center" mb={3}>
      There are no purchase requests to display. Try adjusting your filters or
      create a new purchase request.
    </Typography>
    <IconButton
      onClick={onRefresh}
      color="primary"
      sx={{
        border: "1px solid",
        borderColor: "primary.main",
      }}
    >
      <RefreshCw size={20} />
    </IconButton>
  </Box>
);

const PurchaseRequestList = () => {
  const filter = useSelector((state) => state.purchaseRequest);
  const param = buildParams(filter);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRequest, setSelectedRequest] = React.useState(null);
  const navigate = useNavigate();

  const {
    data: purchaseRequests,
    loading,
    fetchData,
  } = useGetList(
    `v2/purchase-request`,
    true,
    Object.keys(param).length > 0 ? param : undefined,
  );

  const dispatch = useDispatch();

  const handleChangePage = (e, page) => {
    dispatch(setPage(page + 1));
  };
  const handleChangeRowsPerPage = (e) => {
    dispatch(setLimit(e.target.value));
    dispatch(setPage(1));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      default:
        return "default";
    }
  };

  useEffect(() => {
    fetchData(Object.keys(param).length > 0 ? param : undefined);
  }, [filter]);

  const handleRefresh = () => {
    fetchData(Object.keys(param).length > 0 ? param : undefined);
  };

  const handleMenuClick = (event, request) => {
    setAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRequest(null);
  };

  const handleDetail = () => {
    navigate(`/purchase/${selectedRequest._id}`);
  };

  const hasData = purchaseRequests?.data && purchaseRequests.data.length > 0;

  return (
    <>
      {loading ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Required Date</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SkeletonLoader />
            </TableBody>
          </Table>
        </TableContainer>
      ) : !hasData ? (
        <Paper>
          <EmptyState onRefresh={handleRefresh} />
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Required Date</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseRequests?.data?.map((request) => (
                <TableRow key={request._id}>
                  <TableCell>{request.code}</TableCell>
                  <TableCell>
                    {formatDate(request?.purchaseRequestDate)}
                  </TableCell>
                  <TableCell>{formatDate(request?.requiredDate)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request?.relatedSupplier?.name || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {request?.relatedSupplier?.phone || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {request.relatedBranch?.name || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {request?.selectedProducts?.map((product, index) => (
                      <Box key={product._id} mb={1}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          display="block"
                        >
                          {product?.relatedProduct?.name || "N/A"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          {product?.relatedProductItem?.medicineItemName ||
                            "N/A"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="primary"
                          display="block"
                        >
                          {product?.relatedItemUnit?.name} × {product.qty} ={" "}
                          {product?.subtotal.toLocaleString()} MMK
                        </Typography>
                      </Box>
                    ))}
                  </TableCell>
                  <TableCell>{request.total.toLocaleString()} MMK </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={(e) => handleMenuClick(e, request)}
                      size="small"
                      variant="outlined"
                      endIcon={<KeyboardArrowDownIcon />}
                      sx={{
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        "&:hover": {
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          backgroundColor: "#f5f5f5",
                        },
                        textTransform: "none",
                        fontSize: "13px",
                      }}
                    >
                      Actions
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      sx={{
                        "& .MuiMenuItem-root": {
                          flexGrow: 1,
                        },
                      }}
                      slotProps={{
                        paper: {
                          elevation: 0,
                          sx: {
                            mt: 1,
                            borderRadius: 2,
                            minWidth: 140,
                            fontSize: 12,
                            border: "1px solid #f0f0f0",
                          },
                        },
                      }}
                    >
                      <MenuItem onClick={handleDetail.bind(null, request)}>
                        <Typography
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <EyeIcon size={18} />
                          View
                        </Typography>
                      </MenuItem>
                      <MenuItem>
                        <Typography
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <RotateCwIcon size={18} />
                          Approve
                        </Typography>
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {hasData && !loading && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={purchaseRequests?.pagination?.total || 0}
          rowsPerPage={filter.limit}
          page={filter.page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",

            "& .MuiTablePagination-toolbar": {
              display: "flex",
              alignItems: "center",
              minHeight: "52px",
            },

            "& .MuiTablePagination-selectLabel": {
              margin: 0,
              display: "flex",
              alignItems: "center",
            },

            "& .MuiTablePagination-displayedRows": {
              margin: 0,
              display: "flex",
              alignItems: "center",
            },

            "& .MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      )}
    </>
  );
};

export default PurchaseRequestList;
